import { Carte } from '../ui/Primitives';
import { Icone } from '../Icone';
import type { NomsFichiersExport, VolumesRqth } from '../../lib/useExport';

/** Ce qu'on exporte : deux jeux de données distincts, pas deux boutons voisins. */
export type JeuDeDonnees = 'suivi' | 'sifa' | 'rqth';

/** Dans quel format on l'exporte. */
export type FormatExport = 'csv' | 'xlsx';

interface DescriptionJeu {
  cle: JeuDeDonnees;
  titre: string;
  resume: string;
  /** Formats réellement disponibles pour ce jeu de données. */
  formats: FormatExport[];
  /** Mention affichée sous le choix : donnée sensible, portée restreinte. */
  avertissement?: string;
}

const LIBELLES_FORMAT: Record<FormatExport, { titre: string; detail: string }> = {
  csv: { titre: 'CSV', detail: 'tableur, import' },
  xlsx: { titre: 'Excel', detail: '.xlsx, une feuille par promotion' },
};

/** Le classeur RQTH suit le modèle du client : une feuille par antenne, pas par promotion. */
const DETAIL_XLSX_RQTH = '.xlsx, une feuille par antenne';

/**
 * Choix explicite de ce qui va être téléchargé : d'abord le jeu de données,
 * ensuite le format, enfin le nom du fichier qui va arriver.
 *
 * L'écran présentait auparavant trois boutons côte à côte — « Fichier SIFA »,
 * « Classeur Excel », « CSV » — qui mélangeaient deux questions sans réponse
 * lisible : sur quelles données, et sous quelle forme. Un même tableau de suivi
 * a deux formats, et la remontée SIFA n'en a qu'un ; aligner les trois donnait
 * à croire à trois exports comparables.
 *
 * Les formats indisponibles restent visibles et désactivés, avec la raison :
 * les masquer laisserait penser que la plateforme ne sait pas les produire.
 */
export function PanneauExport({
  jeu,
  format,
  onJeu,
  onFormat,
  fichiers,
  perimetre,
  lignesSuivi,
  colonnesSuivi,
  colonnesSifa,
  rqth,
  onTelecharger,
}: {
  jeu: JeuDeDonnees;
  format: FormatExport;
  onJeu: (jeu: JeuDeDonnees) => void;
  onFormat: (format: FormatExport) => void;
  /** Noms calculés par l'API : l'écran annonce, il ne recompose pas. */
  fichiers: NomsFichiersExport | null;
  perimetre: string;
  lignesSuivi: number | null;
  colonnesSuivi: number;
  colonnesSifa: number;
  rqth: VolumesRqth;
  onTelecharger: () => void;
}): JSX.Element {
  const jeux: DescriptionJeu[] = [
    {
      cle: 'suivi',
      titre: 'Tableau de suivi · apprenants',
      resume:
        lignesSuivi === null
          ? `${colonnesSuivi} colonne${colonnesSuivi > 1 ? 's' : ''} retenue${colonnesSuivi > 1 ? 's' : ''}`
          : `${lignesSuivi} dossier${lignesSuivi > 1 ? 's' : ''} · ${colonnesSuivi} colonne${colonnesSuivi > 1 ? 's' : ''} retenue${colonnesSuivi > 1 ? 's' : ''}`,
      formats: ['csv', 'xlsx'],
    },
    {
      cle: 'sifa',
      titre: 'Remontée SIFA',
      resume: `${colonnesSifa} colonnes imposées · déclaration annuelle`,
      formats: ['csv'],
    },
    {
      cle: 'rqth',
      titre: 'Apprenants ayant une attestation RQTH',
      resume: `${rqth.dossiers} dossier${rqth.dossiers > 1 ? 's' : ''} · accompagnement`,
      formats: ['xlsx'],
      avertissement:
        'Donnée de santé : ce tableau ne part jamais dans la synchronisation partagée, ' +
        'et chaque téléchargement est tracé dossier par dossier.',
    },
  ];

  const choisi = jeux.find((entree) => entree.cle === jeu) ?? jeux[0]!;
  const nomFichier = !fichiers
    ? null
    : jeu === 'sifa'
      ? fichiers.sifaCsv
      : jeu === 'rqth'
        ? fichiers.rqthXlsx
        : format === 'xlsx'
          ? fichiers.suiviXlsx
          : fichiers.suiviCsv;

  return (
    <Carte titre="Exporter un fichier" sansBordure>
      <div className="space-y-5 p-4 sm:p-6">
        <fieldset className="space-y-2">
          <legend className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            1 · Quelles données
          </legend>

          {jeux.map((entree) => {
            const actif = entree.cle === jeu;
            return (
              <label
                key={entree.cle}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                  actif
                    ? 'border-codialis-navy bg-codialis-sand'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="jeu-export"
                  className="mt-0.5 shrink-0"
                  checked={actif}
                  onChange={() => onJeu(entree.cle)}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-codialis-navy">
                    {entree.titre}
                  </span>
                  <span className="block text-[11px] text-codialis-gray">{entree.resume}</span>
                  {entree.avertissement && actif && (
                    <span className="mt-1 block text-[11px] text-amber-700">
                      {entree.avertissement}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            2 · Quel format
          </legend>

          {/* `flex-wrap` plutôt qu'une grille à deux colonnes : à 375px, deux
              libellés de format et leur détail ne tiennent pas côte à côte. */}
          <div className="flex flex-wrap gap-2">
            {(['csv', 'xlsx'] as FormatExport[]).map((candidat) => {
              const disponible = choisi.formats.includes(candidat);
              const actif = disponible && candidat === format;
              return (
                <label
                  key={candidat}
                  title={
                    disponible
                      ? undefined
                      : jeu === 'sifa'
                        ? 'La remontée SIFA impose le CSV : l’ordre et le nombre de colonnes sont fixés par la déclaration.'
                        : 'Le tableau RQTH suit le modèle Excel de TOP FORMATION : il ne sort qu’en .xlsx.'
                  }
                  className={`flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 transition ${
                    actif
                      ? 'border-codialis-navy bg-codialis-navy text-white'
                      : disponible
                        ? 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                        : 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="format-export"
                    className="sr-only"
                    checked={actif}
                    disabled={!disponible}
                    onChange={() => onFormat(candidat)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">
                      {LIBELLES_FORMAT[candidat].titre}
                    </span>
                    <span
                      className={`block text-[11px] ${actif ? 'text-white/70' : 'text-codialis-gray'}`}
                    >
                      {!disponible
                        ? `indisponible pour ${jeu === 'sifa' ? 'SIFA' : 'le tableau RQTH'}`
                        : jeu === 'rqth' && candidat === 'xlsx'
                          ? DETAIL_XLSX_RQTH
                          : LIBELLES_FORMAT[candidat].detail}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {/* Ce que le clic va réellement produire, avant de cliquer : périmètre,
            puis nom exact du fichier tel que l'API le posera. */}
        <div className="space-y-1.5 rounded-xl bg-gray-50 p-3 text-[11px] text-codialis-gray">
          <div className="flex justify-between gap-3">
            <span>Périmètre</span>
            <span className="text-right font-medium text-codialis-navy">{perimetre}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span>Fichier</span>
            <span className="min-w-0 break-all text-right font-mono text-[11px] text-codialis-navy">
              {nomFichier ?? '—'}
            </span>
          </div>
          {jeu === 'suivi' && format === 'xlsx' && (
            <p className="pt-1">
              Le classeur reprend la forme des tableaux tenus par TOP FORMATION : une feuille
              par promotion, colonnes groupées immersion, contrat, rupture. La sélection de
              colonnes ne s’y applique pas.
            </p>
          )}
          {jeu === 'sifa' && (
            <p className="pt-1">
              Les 47 colonnes et leur ordre sont imposés par la remontée : la sélection de
              colonnes ne s’y applique pas.
            </p>
          )}
          {jeu === 'rqth' && (
            <>
              <p className="pt-1">
                Le classeur reprend le tableau tenu par TOP FORMATION : une feuille par antenne,
                nom, formation, dates du contrat et observations.
              </p>
              <div className="flex justify-between gap-3">
                <span>Synchronisation partagée</span>
                <span className="text-right font-medium text-codialis-navy">exclue</span>
              </div>
              {rqth.attestationManquante > 0 && (
                <p className="pt-1">
                  {rqth.attestationManquante} dossier
                  {rqth.attestationManquante > 1 ? 's' : ''} déclaré
                  {rqth.attestationManquante > 1 ? 's' : ''} sans attestation au dossier :
                  hors de ce fichier, qui porte sur les attestations, mais à relancer.
                </p>
              )}
            </>
          )}
        </div>

        <button
          type="button"
          onClick={onTelecharger}
          disabled={!nomFichier}
          className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Icone nom="telecharger" className="h-4 w-4" />
          Télécharger {choisi.titre.split(' · ')[0]}
        </button>
      </div>
    </Carte>
  );
}
